import { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"
import { ArrowLeft, Save, X } from "lucide-react"
import { toast } from 'sonner'
import { useUserName, useUserRol, useTienePermiso } from '../App';
import { RadioGroup, RadioGroupItem } from "./ui/radio-group"
import { Label } from "./ui/label"
import { RechazoModal } from "./RechazoModal";
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService';
import { subirImagenPrueba } from '../lib/storage';
import { TejidoCanvas, type TejidoCanvasHandle } from './TejidoCanvas';

interface FormData {
  fecha: string
  imagenUrl: string
  resultadoPlano: string
  resultadoPunto: string
  observaciones: string
  mostrarObservaciones: boolean
  realizo: string
  superviso: string
  numeroSolicitud: string
  tipoTejido: string
}

interface MET7212FormProps {
  pedidoNumero: string;
  pedidoRowId?: string;
  onBack: () => void;
  onSave: (data: any) => void;
  realizoUser?: string;
  initialData?: any;
  mode?: 'create' | 'view';
  onRefreshBackend?: () => void;
  onAutosave?: (result: { rowId: string; nomId: string }) => void;
}

export function MET7212Form({ pedidoNumero, pedidoRowId, onBack, onSave, realizoUser, initialData, mode = 'create', onRefreshBackend, onAutosave }: MET7212FormProps) {
  const globalRol = typeof useUserRol === 'function' ? useUserRol() : undefined;
  const userName = typeof useUserName === 'function' ? useUserName() : '';
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');

  const [formData, setFormData] = useState<FormData>({
    fecha: new Date().toISOString().split('T')[0],
    imagenUrl: "",
    resultadoPlano: "",
    resultadoPunto: "",
    observaciones: "",
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
    realizo: realizoUser || "",
    superviso: "",
    numeroSolicitud: "",
    tipoTejido: "TEJIDO PLANO"
  })

  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>("")
  const [hasDrawing, setHasDrawing] = useState(false)
  const tejidoCanvasRef = useRef<TejidoCanvasHandle>(null)
  const [editMode, setEditMode] = useState(false);
  const [estatus, setEstatus] = useState<string>('');
  const [rechazoModalOpen, setRechazoModalOpen] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [autosaveRowId, setAutosaveRowId] = useState<string>((initialData as any)?.ROW_ID || (initialData as any)?.row_id || '');
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialData as any)?.nomId || '');
  const rowId = autosaveRowId;
  const nomId = autosaveNomId;
  const pedidoDbId = String((initialData as any)?.PEDIDO || (initialData as any)?.pedido || '');
  const isAceptado = estatus === 'aceptado' || estatus === 'rechazado';
  const isReadOnly = mode === 'create' ? false : (mode === 'view' && !editMode);

  useEffect(() => {
    if (realizoUser) setFormData(prev => ({ ...prev, realizo: realizoUser }))
  }, [realizoUser])

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData, mostrarObservaciones: initialData.MOSTRAR_OBSERVACIONES === true }));
      const aceptadoRaw = initialData.ACEPTADO ?? initialData.aceptado ?? '';
      if (typeof aceptadoRaw === 'string') {
        const val = aceptadoRaw.toLowerCase().trim();
        setEstatus(val === 'aceptado' || val === 'rechazado' ? val : '');
      }
      const imgUrl = initialData.imagenUrl || initialData.imagen_url || initialData.IMAGEN_URL || '';
      if (imgUrl) setPreviewUrl(imgUrl);
    }
  }, [initialData]);

  // Object URL for newly selected file (revoked on cleanup)
  useEffect(() => {
    if (!selectedImage) return;
    const url = URL.createObjectURL(selectedImage)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [selectedImage])

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) setSelectedImage(file)
  }

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setPreviewUrl('');
    setFormData(prev => ({ ...prev, imagenUrl: '' }));
  }

  const formPayload = () => formData;

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'TIPO DE LIGAMENTO MET-7.2-12',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onBack()
  }

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      let imagenUrl = formData.imagenUrl;
      if (hasDrawing) {
        const blob = await tejidoCanvasRef.current?.exportBlob();
        if (blob) {
          const file = new File([blob], `tejido-${formData.tipoTejido === 'TEJIDO DE PUNTO' ? 'punto' : 'plano'}.png`, { type: 'image/png' });
          imagenUrl = await subirImagenPrueba(file, pedidoDbId, pedidoNumero, rowId);
        }
      } else if (selectedImage) {
        imagenUrl = await subirImagenPrueba(selectedImage, pedidoDbId, pedidoNumero, rowId);
      }
      onSave({
        ...formData,
        imagenUrl,
        PRUEBA: 'TIPO DE LIGAMENTO MET-7.2-12',
        PEDIDO: pedidoNumero,
      });
    } catch (err: any) {
      toast.error('Error al subir imagen: ' + (err.message || err));
      setSaving(false);
    }
  }

  const handleGuardarEdicion = async () => {
    setSaving(true);
    try {
      let imagenUrl = formData.imagenUrl || previewUrl || '';
      if (hasDrawing) {
        const blob = await tejidoCanvasRef.current?.exportBlob();
        if (blob) {
          const file = new File([blob], `tejido-${formData.tipoTejido === 'TEJIDO DE PUNTO' ? 'punto' : 'plano'}.png`, { type: 'image/png' });
          imagenUrl = await subirImagenPrueba(file, pedidoDbId, pedidoNumero, rowId);
        }
      } else if (selectedImage) {
        imagenUrl = await subirImagenPrueba(selectedImage, pedidoDbId, pedidoNumero, rowId);
      }
      onSave({
        ...formData,
        imagenUrl,
        PRUEBA: 'TIPO DE LIGAMENTO MET-7.2-12',
        PEDIDO: pedidoNumero,
        _EDIT_MODE: true,
      } as any);
    } catch (err: any) {
      toast.error('Error al guardar edición: ' + (err.message || err));
      setSaving(false);
    }
  }

  const handleAccion = async (accion: 'aceptado' | 'rechazado', razonRechazo?: string) => {
    const confirmMsg = accion === 'aceptado'
      ? '¿Estás seguro de que quieres aceptar la prueba MET-7.2-12? Esta acción no podrá editarse ni borrarse.'
      : '¿Estás seguro de que quieres rechazar la prueba MET-7.2-12? Esta acción no podrá editarse ni borrarse.';
    if (!window.confirm(confirmMsg)) return;
    try {
      if (accion === 'rechazado') setRechazoLoading(true);
      const supervisor = userName || (typeof window !== 'undefined' ? localStorage.getItem('userName') || '' : '');
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, accion === 'rechazado' ? razonRechazo : undefined, supervisor, nomId);
      setEstatus(accion);
      setEditMode(false);
      setRechazoModalOpen(false);
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      toast.error('Error: ' + (err.message || err));
    } finally {
      setRechazoLoading(false);
    }
  }

  const canAcceptReject = puedeRevisar && estatus !== 'aceptado' && estatus !== 'rechazado' && !editMode;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        <div>
          <h1 className="font-bold text-lg">DETERMINACIÓN DEL TIPO DE LIGAMENTO MET-7.2-12</h1>
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-center">DETERMINACIÓN DEL TIPO DE LIGAMENTO MET-7.2-12</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-2 border-foreground">
              <tbody>
                <tr>
                  <td className="border-2 border-foreground p-4 font-medium bg-muted w-1/2">FECHA:</td>
                  <td className="border-2 border-foreground p-2 w-1/2">
                    <Input
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => handleInputChange('fecha', e.target.value)}
                      className="border-0 bg-transparent focus:ring-0 focus:outline-none"
                      disabled={isReadOnly}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border-2 border-foreground p-4 font-medium bg-muted w-1/2">NÚMERO DE SOLICITUD:</td>
                  <td className="border-2 border-foreground p-2 w-1/2">
                    <Input
                      value={formData.numeroSolicitud}
                      onChange={e => handleInputChange('numeroSolicitud', e.target.value)}
                      className="max-w-xs"
                      disabled={isReadOnly}
                    />
                  </td>
                </tr>
                <tr>
                  <td colSpan={2} className="border-2 border-foreground p-4 font-medium bg-muted text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="mb-1">TIPO DE TEJIDO</span>
                      <RadioGroup
                        value={formData.tipoTejido}
                        onValueChange={v => handleInputChange('tipoTejido', v)}
                        className="flex justify-center gap-8"
                        disabled={isReadOnly}
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="TEJIDO PLANO" id="tejido-plano" />
                          <Label htmlFor="tejido-plano">Tejido plano</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="TEJIDO DE PUNTO" id="tejido-punto" />
                          <Label htmlFor="tejido-punto">Tejido de punto</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </td>
                </tr>

                {/* Lienzo para dibujar el ligamento sobre la cuadrícula/puntos de referencia */}
                {!isReadOnly && (
                  <tr>
                    <td className="border-2 border-foreground p-4 text-center" colSpan={2}>
                      <div className="flex flex-col items-center gap-2">
                        <p className="font-medium">
                          {formData.tipoTejido === 'TEJIDO DE PUNTO' ? 'Tejido de punto' : 'Tejido plano'}
                        </p>
                        <div className="overflow-x-auto max-w-full">
                          <TejidoCanvas
                            ref={tejidoCanvasRef}
                            tipo={formData.tipoTejido === 'TEJIDO DE PUNTO' ? 'punto' : 'plano'}
                            disabled={isReadOnly}
                            onDrawingChange={setHasDrawing}
                            initialImageUrl={previewUrl || formData.imagenUrl}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => { tejidoCanvasRef.current?.clear(); setHasDrawing(false); }}
                        >
                          Limpiar dibujo
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Área de Imagen (respaldo opcional, se oculta si ya se dibujó sobre la cuadrícula) */}
                {!hasDrawing && (
                  <tr>
                    <td className="border-2 border-foreground p-2 text-center" colSpan={2}>
                      <p className="text-xs text-muted-foreground mb-1">O adjuntar una foto del tejido (opcional)</p>
                      <div className="flex flex-col items-center justify-center gap-1.5 bg-muted/50 rounded border border-dashed border-border p-2">
                        {previewUrl ? (
                          <div className="space-y-1 flex flex-col items-center">
                            <img
                              src={previewUrl}
                              alt="Imagen del tejido"
                              className="max-w-full max-h-24 object-contain rounded"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                            <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline">
                              Ver imagen en nueva pestaña
                            </a>
                            {!isReadOnly && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-800 hover:bg-red-50 h-6 px-2"
                                onClick={handleRemoveImage}
                              >
                                <X className="h-3 w-3 mr-1" />
                                Quitar imagen
                              </Button>
                            )}
                          </div>
                        ) : (
                          !isReadOnly ? (
                            <p className="text-xs text-muted-foreground">Selecciona una imagen del tejido (JPG, PNG)</p>
                          ) : (
                            <p className="text-xs text-muted-foreground">Sin imagen adjunta</p>
                          )
                        )}
                        {/* File input visible when not read-only */}
                        {!isReadOnly && (
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="max-w-md mx-auto h-8 text-xs"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                <tr>
                  <td className="border-2 border-foreground p-4 bg-muted w-full" colSpan={2}>
                    <div className="space-y-2">
                      <p className="font-medium">RESULTADO:</p>
                      <Input
                        value={formData.resultadoPunto}
                        onChange={(e) => handleInputChange('resultadoPunto', e.target.value)}
                        className="border-0 bg-background focus:ring-0 focus:outline-none w-full"
                        disabled={isReadOnly}
                      />
                    </div>
                  </td>
                </tr>

                <tr>
                  <td className="border-2 border-foreground p-4 font-medium bg-muted flex items-center gap-2" colSpan={2}>
                    OBSERVACIONES:
                    <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                      Mostrar en Reporte Cliente
                      <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                        onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="border-2 border-foreground p-2" colSpan={2}>
                    <Textarea
                      placeholder="Ingrese las observaciones aquí..."
                      value={formData.observaciones}
                      onChange={(e) => handleInputChange('observaciones', e.target.value)}
                      className="border-0 bg-transparent focus:ring-0 focus:outline-none min-h-[100px] resize-none"
                      readOnly={isReadOnly}
                    />
                  </td>
                </tr>

                <tr>
                  <td className="border-2 border-foreground p-4 bg-muted w-1/2">
                    <div className="space-y-2">
                      <p className="font-medium">REALIZÓ:</p>
                      <Input
                        value={formData.realizo}
                        readOnly
                        className="border-0 bg-muted-foreground/10 cursor-not-allowed"
                      />
                    </div>
                  </td>
                  <td className="border-2 border-foreground p-4 bg-muted w-1/2">
                    <div className="space-y-2">
                      <p className="font-medium">SUPERVISÓ:</p>
                      <Input
                        value={formData.superviso}
                        readOnly
                        className="border-0 bg-muted-foreground/10 cursor-not-allowed"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center gap-4 mt-8">
        <Button variant="outline" onClick={handleBackFlush} disabled={saving}>
          Cancelar
        </Button>

        {mode === 'create' && (
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        )}

        {mode === 'view' && (
          <>
            {canAcceptReject && (
              <>
                <Button variant="default" onClick={() => handleAccion('aceptado')}>
                  Aceptar
                </Button>
                <Button variant="destructive" onClick={() => setRechazoModalOpen(true)}>
                  Rechazar
                </Button>
                <Button variant="secondary" onClick={() => setEditMode(true)}>
                  Editar
                </Button>
              </>
            )}
            {editMode && (
              <Button variant="default" onClick={handleGuardarEdicion} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar Edición'}
              </Button>
            )}
          </>
        )}
      </div>

      <RechazoModal
        open={rechazoModalOpen}
        onClose={() => setRechazoModalOpen(false)}
        loading={rechazoLoading}
        onSubmit={(razon) => handleAccion('rechazado', razon)}
      />
    </div>
  )
}
